/**
 * Curated interests / hobbies — closed vocab with Lucide icons.
 *
 * Selection is closed: users pick from this list, no free-form input.
 * (The wizard previously allowed free-form chip entry; that's
 * replaced. Personality + Bio prompts are where users express
 * themselves freely.)
 *
 * Icon names reference Lucide. When adding new entries, prefer an
 * icon that exists in both `lucide-react` and `lucide-react-native`
 * — they share the same name set.
 *
 * Grouping (informal): the list is ordered so similar interests
 * sit together when rendered as chips. Card UI doesn't honor
 * subgrouping today.
 */

import type { TraitOption } from '../types.js';

export const INTEREST_OPTIONS: readonly TraitOption[] = [
  // ─── Faith / community ──────────────────────────────────────────
  { value: 'quran_study', labelKey: 'interest.quran_study', icon: 'book-open' },
  { value: 'islamic_history', labelKey: 'interest.islamic_history', icon: 'scroll' },
  { value: 'volunteering', labelKey: 'interest.volunteering', icon: 'heart-handshake' },
  { value: 'mosque_community', labelKey: 'interest.mosque_community', icon: 'landmark' },

  // ─── Outdoors / fitness ─────────────────────────────────────────
  { value: 'hiking', labelKey: 'interest.hiking', icon: 'mountain' },
  { value: 'running', labelKey: 'interest.running', icon: 'footprints' },
  { value: 'cycling', labelKey: 'interest.cycling', icon: 'bike' },
  { value: 'swimming', labelKey: 'interest.swimming', icon: 'waves' },
  { value: 'gym', labelKey: 'interest.gym', icon: 'dumbbell' },
  { value: 'martial_arts', labelKey: 'interest.martial_arts', icon: 'shield' },
  { value: 'yoga', labelKey: 'interest.yoga', icon: 'flower' },
  { value: 'camping', labelKey: 'interest.camping', icon: 'tent' },

  // ─── Creative ──────────────────────────────────────────────────
  { value: 'reading', labelKey: 'interest.reading', icon: 'book' },
  { value: 'writing', labelKey: 'interest.writing', icon: 'pen-tool' },
  { value: 'poetry', labelKey: 'interest.poetry', icon: 'feather' },
  { value: 'painting', labelKey: 'interest.painting', icon: 'palette' },
  { value: 'calligraphy', labelKey: 'interest.calligraphy', icon: 'highlighter' },
  { value: 'photography', labelKey: 'interest.photography', icon: 'camera' },
  { value: 'filmmaking', labelKey: 'interest.filmmaking', icon: 'film' },
  { value: 'music', labelKey: 'interest.music', icon: 'music' },
  { value: 'singing', labelKey: 'interest.singing', icon: 'mic' },

  // ─── Food & home ───────────────────────────────────────────────
  { value: 'cooking', labelKey: 'interest.cooking', icon: 'chef-hat' },
  { value: 'baking', labelKey: 'interest.baking', icon: 'cake-slice' },
  { value: 'tea', labelKey: 'interest.tea', icon: 'cup-soda' },
  { value: 'coffee', labelKey: 'interest.coffee', icon: 'coffee' },
  { value: 'gardening', labelKey: 'interest.gardening', icon: 'sprout' },

  // ─── Travel & culture ──────────────────────────────────────────
  { value: 'travel', labelKey: 'interest.travel', icon: 'plane' },
  { value: 'languages', labelKey: 'interest.languages', icon: 'languages' },
  { value: 'history', labelKey: 'interest.history', icon: 'landmark' },
  { value: 'museums', labelKey: 'interest.museums', icon: 'building-2' },

  // ─── Tech / craft ──────────────────────────────────────────────
  { value: 'coding', labelKey: 'interest.coding', icon: 'code' },
  { value: 'gaming', labelKey: 'interest.gaming', icon: 'gamepad-2' },
  { value: 'chess', labelKey: 'interest.chess', icon: 'crown' },
  { value: 'puzzles', labelKey: 'interest.puzzles', icon: 'puzzle' },
  { value: 'diy', labelKey: 'interest.diy', icon: 'hammer' },

  // ─── Sport (watching / playing) ────────────────────────────────
  { value: 'football', labelKey: 'interest.football', icon: 'circle-dot' },
  { value: 'cricket', labelKey: 'interest.cricket', icon: 'circle' },
  { value: 'basketball', labelKey: 'interest.basketball', icon: 'circle' },
  { value: 'tennis', labelKey: 'interest.tennis', icon: 'circle' },

  // ─── Family & kids ─────────────────────────────────────────────
  { value: 'family_time', labelKey: 'interest.family_time', icon: 'home' },
  { value: 'mentoring_kids', labelKey: 'interest.mentoring_kids', icon: 'graduation-cap' },

  // ─── Animals & nature ──────────────────────────────────────────
  { value: 'cats', labelKey: 'interest.cats', icon: 'cat' },
  { value: 'dogs', labelKey: 'interest.dogs', icon: 'dog' },
  { value: 'horses', labelKey: 'interest.horses', icon: 'rabbit' },
  { value: 'birdwatching', labelKey: 'interest.birdwatching', icon: 'bird' },

  // ─── Lifestyle & wellness ──────────────────────────────────────
  { value: 'meditation', labelKey: 'interest.meditation', icon: 'lotus' },
  { value: 'minimalism', labelKey: 'interest.minimalism', icon: 'square' },
  { value: 'journaling', labelKey: 'interest.journaling', icon: 'notebook-pen' },
  { value: 'fashion', labelKey: 'interest.fashion', icon: 'shirt' },

  // ─── Misc ──────────────────────────────────────────────────────
  { value: 'podcasts', labelKey: 'interest.podcasts', icon: 'mic' },
  { value: 'movies', labelKey: 'interest.movies', icon: 'clapperboard' },
  { value: 'cars', labelKey: 'interest.cars', icon: 'car' },
] as const;

/** Stable set for O(1) validity lookups. Useful in schema validators. */
export const INTEREST_VALUES: ReadonlySet<string> = new Set(INTEREST_OPTIONS.map((o) => o.value));
